import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { certificate, enrollment } from "../../store";
import { CertificateCard } from "./CertificateCard";
import { CertificatePreviewModal } from "./CertificatePreviewModal";
import { CertificatesEmpty } from "./CertificatesEmpty";
import {
  CERTIFICATE_TABS,
  filterCertificatesByTab,
} from "./certificateListConfig";
import { ensureCertificateIssued } from "../../utils/certificate";
import "./CertificateList.css";

export const CertificateList = () => {
  const [activeTab, setActiveTab] = useState("all");
  const [previewTarget, setPreviewTarget] = useState(null);
  const isBackfillingRef = useRef(false);

  const getCertificatesByUser = certificate((state) => state.getCertificatesByUser);
  const clearCertificates = certificate((state) => state.clearCertificates);
  const certificates = certificate((state) => state.certificates);
  const isLoading = certificate((state) => state.isLoading);
  const checkCertificate = certificate((state) => state.checkCertificate);
  const createCertificate = certificate((state) => state.createCertificate);

  const getUserEnrollments = enrollment((state) => state.getUserEnrollments);
  const enrollments = enrollment((state) => state.enrollments);
  const isEnrollmentLoading = enrollment((state) => state.isLoading);

  const myUser = useMemo(() => {
    const data = localStorage.getItem("auth-storage");
    return data ? JSON.parse(data).state?.user : null;
  }, []);
  const myUserId = myUser?.userId;
  const myUserName = myUser?.name;

  useEffect(() => {
    if (!myUserId) return;
    clearCertificates();
    getCertificatesByUser(myUserId);
    getUserEnrollments(myUserId);
  }, [myUserId, clearCertificates, getCertificatesByUser, getUserEnrollments]);

  // 수료 처리는 됐지만 어떤 이유로 수료증 레코드가 아직 없는 강의를 찾아
  // 마이페이지 진입 즉시 채워 넣는다 (영상 시청 중 생성이 실패/누락된 경우 대비).
  useEffect(() => {
    if (!myUserId || isLoading || isEnrollmentLoading) return;
    if (isBackfillingRef.current) return;

    const existingCourseIds = new Set(
      (certificates ?? [])
        .map((item) => Number(item.course_id))
        .filter((id) => !Number.isNaN(id))
    );

    const missing = (enrollments ?? []).filter(
      (item) =>
        item.is_completed &&
        !item.canceled_at &&
        !existingCourseIds.has(Number(item.course_id))
    );

    if (missing.length === 0) return;

    isBackfillingRef.current = true;

    (async () => {
      let createdAny = false;
      for (const item of missing) {
        try {
          const created = await ensureCertificateIssued({
            userId: myUserId,
            courseId: item.course_id,
            userName: myUserName,
            checkCertificate,
            createCertificate,
          });
          if (created) createdAny = true;
        } catch (error) {
          console.error("수료증 자동 생성 실패:", error);
        }
      }
      if (createdAny) {
        await getCertificatesByUser(myUserId);
      }
      isBackfillingRef.current = false;
    })();
  }, [
    myUserId,
    myUserName,
    certificates,
    enrollments,
    isLoading,
    isEnrollmentLoading,
    checkCertificate,
    createCertificate,
    getCertificatesByUser,
  ]);

  // 백엔드 수료증 응답에는 교육 수료일이 없으므로, 같은 강의의 수강 정보에서
  // 실제 수료 완료일(completed_at)을 course_id 기준으로 매칭해 채워 넣는다.
  const completionByCourseId = useMemo(() => {
    const map = new Map();
    (enrollments ?? []).forEach((item) => {
      const courseId = Number(item.course_id);
      if (Number.isNaN(courseId) || !item.completed_at) return;
      map.set(courseId, item.completed_at);
    });
    return map;
  }, [enrollments]);

  const enrichedCertificates = useMemo(
    () =>
      (certificates ?? []).map((item) => {
        const completedAt = completionByCourseId.get(Number(item.course_id));
        return completedAt ? { ...item, completed_at: completedAt } : item;
      }),
    [certificates, completionByCourseId]
  );

  const visibleCertificates = useMemo(
    () => filterCertificatesByTab(enrichedCertificates, activeTab),
    [enrichedCertificates, activeTab]
  );

  const hasAnyCertificate = enrichedCertificates.length > 0;

  const handleOpenPreview = useCallback((certificateId, listItem) => {
    setPreviewTarget({ certificateId, listItem });
  }, []);

  const handleClosePreview = useCallback(() => {
    setPreviewTarget(null);
  }, []);

  const handleCertificateIssued = useCallback(() => {
    if (!myUserId) return;
    clearCertificates();
    getCertificatesByUser(myUserId);
  }, [myUserId, clearCertificates, getCertificatesByUser]);

  return (
    <div className="mypage-certificates">
      {hasAnyCertificate && (
        <>
          <div
            className="mypage-certificates__tabs"
            role="tablist"
            aria-label="수료증 필터"
          >
            {CERTIFICATE_TABS.map(({ key, label }) => (
              <button
                key={key}
                type="button"
                role="tab"
                aria-selected={activeTab === key}
                className={`mypage-certificates__tab${
                  activeTab === key ? " mypage-certificates__tab--active" : ""
                }`}
                onClick={() => setActiveTab(key)}
              >
                {label}
              </button>
            ))}
          </div>

          {isLoading ? (
            <p className="mypage-certificates__loading">불러오는 중...</p>
          ) : visibleCertificates.length === 0 ? (
            <p className="mypage-certificates__empty-tab">
              해당 조건의 수료증이 없습니다.
            </p>
          ) : (
            <ul className="mypage-certificates__grid">
              {visibleCertificates.map((item) => (
                <li key={item.id ?? item.certificate_id}>
                  <CertificateCard
                    certificate={item}
                    onPreview={handleOpenPreview}
                  />
                </li>
              ))}
            </ul>
          )}
        </>
      )}

      {!isLoading && !hasAnyCertificate && <CertificatesEmpty />}
      {isLoading && !hasAnyCertificate && (
        <p className="mypage-certificates__loading">불러오는 중...</p>
      )}

      {previewTarget && (
        <CertificatePreviewModal
          certificateId={previewTarget.certificateId}
          listItem={previewTarget.listItem}
          onClose={handleClosePreview}
          onIssued={handleCertificateIssued}
        />
      )}
    </div>
  );
};
